import Locator from '../../../../../shared/Locator';
import type { SnippetDetails } from '../../../../../shared/snippets';

export type SnippetConversion = {
  fetcher: string;
  packageName: string;
  revision: string;
  locator: string;
  vendoredPath: string;
};

/**
 * Finds the common directory prefix shared by all paths in the given array.
 *
 * For a single path, returns its parent directory.
 * For multiple paths, returns the deepest directory that contains all of them.
 * Falls back to '/' if no common prefix exists.
 *
 * @example
 * findVendoredPathFromMatchPaths(['/src/foo/bar.h', 'src/foo/baz.h']) // '/src/foo/'
 * findVendoredPathFromMatchPaths(['/src/foo/bar.h']) // '/src/foo/'
 * findVendoredPathFromMatchPaths(['/src/foo/bar/quux-test.h', '/src/foo/bar/quux-prod.h']) // '/src/foo/bar/'
 * findVendoredPathFromMatchPaths(['/a/b/c.h', '/x/y/z.h']) // '/'
 */
export const findVendoredPathFromMatchPaths = (matchPaths: string[]): string => {
  const commonPrefix = (str1: string, str2: string): string => {
    // if either is empty, then `/` will be the common prefix
    if (str1.length === 0 || str2.length === 0) {
      return '/';
    }

    let i = 0;
    while (str1.at(i) === str2.at(i)) {
      i++;
    }

    return str1.slice(0, i);
  };

  let vendoredPath = matchPaths.at(0) ?? '';

  if (matchPaths.length > 1) {
    // iteratively finds a common prefix by comparing the adjacent path with previous prefix
    vendoredPath = matchPaths.reduce(commonPrefix);
  }

  const lastSepIndex = vendoredPath.lastIndexOf('/');

  // handle cases where either there was a single match (e.g. '/src/foo.h')
  // or where there are multiple file matches with a shared prefix (e.g. '/src/foo-test.h' and `/src/foo-prod.h`)
  if (lastSepIndex >= 0) {
    vendoredPath = vendoredPath.slice(0, lastSepIndex + 1) ?? '/';
  }

  return vendoredPath;
};

export const verifySnippetDetailsForConversion = (
  snippet: SnippetDetails | undefined,
): { shouldDisableConversion: boolean, disabledConversionMessage?: string } => {
  if (snippet) {
    if (snippet.locator) {
      const locatorParts = Locator.parse(snippet.locator ?? '');

      if (locatorParts.fetcher && locatorParts.package && locatorParts.revision) {
        return { shouldDisableConversion: false };
      }

      return {
        shouldDisableConversion: true,
        disabledConversionMessage: `unable convert locator: '${snippet.locator}' to vendored dependency`,
      };
    }

    return {
      shouldDisableConversion: true,
      disabledConversionMessage: 'unable to convert snippet; snippet is missing locator',
    };
  }

  return {
    shouldDisableConversion: true,
    disabledConversionMessage: 'unable to convert snippet; cannot fetch snippet',
  };
};

export const snippetDetailsToSnippetConversion = (snippet: SnippetDetails): SnippetConversion | null => {
  const vendoredPath = findVendoredPathFromMatchPaths(snippet.matches.map(m => m.path));
  const locatorParts = Locator.parse(snippet.locator ?? '');

  if (locatorParts.fetcher && locatorParts.package && locatorParts.revision) {
    return {
      fetcher: locatorParts.fetcher,
      packageName: locatorParts.package,
      revision: locatorParts.revision,
      locator: snippet.locator ?? '',
      vendoredPath,
    };
  }

  return null;
};
